import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Eye, Play, ThumbsUp, X } from 'lucide-react';

import { Reveal } from '@/components/reveal';
import { USE_CASE_DEMOS, type DemoSortKey } from '@/config/use-case-demos';
import { useLocale } from '@/i18n/locale-context';
import { cn } from '@/lib/utils';

function DemoAvatar({ name, hue }: { name: string; hue: number }) {
  const initial = name.charAt(0).toUpperCase();

  return (
    <span
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white"
      style={{ backgroundColor: `hsl(${hue} 45% 45%)` }}
      aria-hidden
    >
      {initial}
    </span>
  );
}

function VideoPlaceholder({ label }: { label: string }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/20">
      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-border/60 bg-background/80 shadow-sm backdrop-blur-sm">
        <Play className="ml-0.5 h-4 w-4 text-muted-foreground" fill="currentColor" />
      </span>
      <span className="text-[11px] text-muted-foreground">{label}</span>
    </div>
  );
}

interface VideoModalProps {
  demo: (typeof USE_CASE_DEMOS)[number];
  title: string;
  onClose: () => void;
}

function VideoModal({ demo, title, onClose }: VideoModalProps) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-1.5 sm:p-2"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" aria-hidden />
      <div
        className="relative w-full max-w-[1400px] overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/60 text-white/80 transition-colors hover:bg-black/80 hover:text-white"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>
        <video
          className="max-h-[96vh] w-full bg-black"
          src={demo.videoUrl ?? undefined}
          poster={demo.posterUrl ?? undefined}
          controls
          autoPlay
          playsInline
        />
      </div>
    </div>
  );
}

interface DemoVideoCardProps {
  demo: (typeof USE_CASE_DEMOS)[number];
  category: string;
  title: string;
  description: string;
  author: string;
  videoPlaceholder: string;
  delay: number;
  onPlay: () => void;
}

function DemoVideoCard({
  demo,
  category,
  title,
  description,
  author,
  videoPlaceholder,
  delay,
  onPlay,
}: DemoVideoCardProps) {
  return (
    <Reveal delay={delay}>
      <article className="group flex h-full flex-col">
        <button
          type="button"
          className="relative block w-full overflow-hidden rounded-xl border border-border/60 bg-card/30 text-left shadow-sm transition-all duration-300 group-hover:border-primary/30 group-hover:shadow-md"
          onClick={onPlay}
          aria-label={`${title} ${videoPlaceholder}`}
        >
          <div className="relative aspect-video overflow-hidden bg-muted/10">
            {demo.posterUrl ? (
              <img
                src={demo.posterUrl}
                alt={title}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                loading="lazy"
              />
            ) : (
              <VideoPlaceholder label={videoPlaceholder} />
            )}
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/20">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/40 bg-black/50 shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-0.5 h-5 w-5 text-white" fill="currentColor" />
              </span>
            </span>
          </div>
        </button>

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <DemoAvatar name={author} hue={demo.accentHue} />
            <span className="truncate text-xs text-muted-foreground">{author}</span>
          </div>
          <div className="flex shrink-0 items-center gap-3 text-xs text-muted-foreground/70">
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3 w-3" strokeWidth={1.5} />
              {demo.views}
            </span>
            <span className="inline-flex items-center gap-1">
              <ThumbsUp className="h-3 w-3" strokeWidth={1.5} />
              {demo.likes}
            </span>
          </div>
        </div>

        <h3 className="mt-2 line-clamp-2 text-sm font-semibold leading-snug tracking-tight">
          <span className="text-primary/90">【{category}】</span>
          {title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-xs leading-5 text-muted-foreground">{description}</p>
      </article>
    </Reveal>
  );
}

export function UseCaseDemosSection() {
  const { t } = useLocale();
  const [sortKey, setSortKey] = useState<DemoSortKey>('featured');
  const [sortOpen, setSortOpen] = useState(false);

  const sortedDemoIds = useMemo(() => {
    const ids = USE_CASE_DEMOS.map((d) => d.id);
    const messages = t.useCaseDemos.items;

    if (sortKey === 'plugin') {
      return [...ids].sort((a, b) => {
        const catA = messages[a]?.category ?? '';
        const catB = messages[b]?.category ?? '';
        return catA.localeCompare(catB, undefined, { sensitivity: 'base' });
      });
    }

    if (sortKey === 'recent') {
      return [...ids].reverse();
    }

    return ids;
  }, [sortKey, t.useCaseDemos.items]);

  const sortLabel = t.useCaseDemos.sortOptions[sortKey];
  const [activeDemo, setActiveDemo] = useState<(typeof USE_CASE_DEMOS)[number] | null>(
    null
  );

  return (
    <section id="demos" className="scroll-mt-24 px-6 py-20 sm:px-10 sm:py-28">
      <div className="mx-auto w-full max-w-7xl">
        <Reveal className="mx-auto max-w-2xl text-center">
          <p className="section-eyebrow">{t.useCaseDemos.eyebrow}</p>
          <h2 className="mt-4 font-display text-3xl leading-[1.12] tracking-tight sm:text-4xl">
            {t.useCaseDemos.title}
          </h2>
          <p className="mt-4 text-sm leading-6 text-muted-foreground sm:text-base">
            {t.useCaseDemos.description}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex justify-end">
          <div className="relative">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-card/40 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-border hover:text-foreground"
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
              onClick={() => setSortOpen((open) => !open)}
            >
              {t.useCaseDemos.sortLabel}
              <span className="text-foreground">{sortLabel}</span>
              <ChevronDown
                className={cn('h-3.5 w-3.5 transition-transform', sortOpen && 'rotate-180')}
              />
            </button>
            {sortOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  aria-hidden
                  onClick={() => setSortOpen(false)}
                />
                <ul
                  role="listbox"
                  className="absolute right-0 z-20 mt-1 min-w-[8rem] overflow-hidden rounded-lg border border-border/60 bg-popover py-1 shadow-lg"
                >
                  {(Object.keys(t.useCaseDemos.sortOptions) as DemoSortKey[]).map((key) => (
                    <li key={key} role="option" aria-selected={sortKey === key}>
                      <button
                        type="button"
                        className={cn(
                          'w-full px-3 py-1.5 text-left text-xs transition-colors hover:bg-accent/50',
                          sortKey === key ? 'text-foreground font-medium' : 'text-muted-foreground'
                        )}
                        onClick={() => {
                          setSortKey(key);
                          setSortOpen(false);
                        }}
                      >
                        {t.useCaseDemos.sortOptions[key]}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {sortedDemoIds.map((id, index) => {
            const demo = USE_CASE_DEMOS.find((d) => d.id === id);
            const message = t.useCaseDemos.items[id];
            if (!demo || !message) return null;

            return (
              <DemoVideoCard
                key={demo.id}
                demo={demo}
                category={message.category}
                title={message.title}
                description={message.description}
                author={message.author}
                videoPlaceholder={t.useCaseDemos.videoPlaceholder}
                delay={index * 0.06}
                onPlay={() => setActiveDemo(demo)}
              />
            );
          })}
        </div>
      </div>

      {activeDemo && (
        <VideoModal
          demo={activeDemo}
          title={t.useCaseDemos.items[activeDemo.id]?.title ?? ''}
          onClose={() => setActiveDemo(null)}
        />
      )}
    </section>
  );
}
